import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { CalendarDropdown } from '@/ui/common/Dropdowns/CalendarDropdown';
import { BrandLoader } from '@/ui/common/Loaders/BrandLoader';
import { RadioDropdown } from '@/ui/common/Dropdowns/RadioDropdown';
import { EmptyDataText } from '@/ui/common/Texts/EmptyDataText';
import { ECalendarView, TActivity } from '@/domain/Activities.domain';
import { useScrollIntoView } from '@/service/hooks/useScrollIntoView';
import { DaysLine } from './DaysLine';
import { CalendarColumn } from './CalendarColumn';
import { calendarViewOptions } from '../data';
import { useCalendarFullStore } from '@/service/state/FullCalendar/useFullCalendarStore';
import { useWindowWidth } from '@/service/hooks/useWindowWidth';
import { getOneWeekDays } from '@/service/utils/getOneWeekDays';
import { ActiveModifiers } from 'react-day-picker';
import { isPastDay } from '@/service/utils/isPastDay';
import { format, startOfDay } from 'date-fns';
import { MONTH_FORMAT_DATE } from '@/service/constants/dates';
import { newDateFromString } from '@/service/utils/getDateByTimezone';
import { useCartStore } from '@/service/state/Cart/useCartStore';
import { ECartActionType } from '@/service/state/Cart/domain';
import { ToastScreen } from '@/ui/components/CartContent/core/ToastScreen';
import Link from 'next/link';
import { Toast } from '@/ui/common/Toast/Toast';

type TCalendarAttractions = {
    activities: TActivity[];
    partnerSlug: string;
    activityIds: string;
    setHideButtons: Dispatch<SetStateAction<boolean>>;
};

export const CalendarAttractions = ({
    partnerSlug,
    activityIds,
    activities,
    setHideButtons,
}: TCalendarAttractions) => {
    const {
        isActivitiesLoading,
        isTimeLoading,
        initAvailability,
        listToShow,
        viewBy,
        changeViewMode,
        date,
        updateDate,
        prevWeek,
        nextWeek,
    } = useCalendarFullStore();

    const { success } = useCartStore();

    const { mobile, desktop } = useWindowWidth();
    const [selectedColumn, setSelectedColumn] = useState(0);
    const [calendarIsShown, setCalendarIsShown] = useState(false);
    const [pageMonth, setPageMonth] = useState('');

    const calendarViewRef = useRef<HTMLDivElement>(null);
    const isDateLoading = isActivitiesLoading || isTimeLoading;

    useScrollIntoView({
        elementRef: calendarViewRef,
        scrollable: !isActivitiesLoading,
        enableScroll: !isActivitiesLoading,
    });

    const handleDayMouseEnter = useCallback(
        (d: Date, _: ActiveModifiers, e: React.MouseEvent) => {
            if (!date.from || (date.from && d.valueOf() < date.from.valueOf())) return;
            const openedMonthDate = newDateFromString(pageMonth);

            openedMonthDate.setDate(Number(e.currentTarget.innerHTML));

            const oneWeekDays = getOneWeekDays(openedMonthDate);

            const daysInButtonElement = document.querySelectorAll('.style_day__DC2_I');
            const targetDaysInButtonElement = Array.from(daysInButtonElement).filter((el) =>
                oneWeekDays.includes(parseInt(el.innerHTML))
            );

            for (let i = 0; i < targetDaysInButtonElement.length; i++) {
                targetDaysInButtonElement[i].classList.add('hovered-days');
            }
        },
        [date.from, pageMonth]
    );

    const handleDayMouseLeave = useCallback(() => {
        const days = document.querySelectorAll('.style_day__DC2_I');
        days.forEach((day) => {
            if (day.classList.contains('hovered-days')) {
                day.classList.remove('hovered-days');
            }
        });
    }, []);

    const selectNextAvailDay = () => {
        if (nextAvailIndex > 4) {
            const el = document.querySelector('#calendar_days_line');

            if (!el) {
                return;
            }

            if (el.scrollLeft + el.clientWidth + 10 <= el.scrollWidth) {
                el.scrollTo({
                    left: el.scrollWidth,
                    behavior: 'smooth',
                });
            }
        }

        if (nextAvailIndex > 0) {
            setSelectedColumn(nextAvailIndex);
        }
    };

    useEffect(() => {
        initAvailability(activities, activityIds, partnerSlug);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setSelectedColumn(1);
    }, [date.from]);

    const list = mobile ? [listToShow[selectedColumn - 1]] : listToShow;
    const availList = listToShow.map((item) => !!item.length);
    const nextAvailIndex = availList.findIndex((item, idx) => idx > selectedColumn - 1 && item) + 1;

    const selectedDate = date.week[selectedColumn - 1]?.date || new Date();

    useEffect(() => {
        if (availList.length && availList[0] === false && nextAvailIndex !== 1) {
            selectNextAvailDay();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availList.length]);

    useEffect(() => {
        return () => {
            document.body.style.overflowY = 'auto';
        };
    }, []);

    return (
        <>
            <Toast
                show={!!success.message && success.actionType === ECartActionType.ADD_ITEM_TO_CART}
                screen={
                    <ToastScreen
                        title={success.message}
                        description={
                            <Link
                                href={`${partnerSlug}/checkout/`}
                                className="tx-large-medium block w-full text-brand underline"
                            >
                                Checkout now
                            </Link>
                        }
                    />
                }
            />
            <div id="calendar-viewport" className="relative top-3 z-10 mt-4 flex-grow px-0 sm:px-8">
                <div className="relative mx-auto flex h-[45px] w-full max-w-[1400px] flex-col items-center justify-center">
                    <div className="absolute bottom-0 right-0 sm:-right-8 lg:left-2.5">
                        <RadioDropdown
                            caption={'View by'}
                            radioOptions={calendarViewOptions}
                            chosenOption={viewBy}
                            setChosenOption={(view) => changeViewMode(view as ECalendarView)}
                        />
                    </div>
                    <CalendarDropdown
                        mode="single"
                        isLoading={isDateLoading}
                        isShown={calendarIsShown}
                        showCalendar={() => (
                            setCalendarIsShown(true),
                            setPageMonth(format(selectedDate, MONTH_FORMAT_DATE))
                        )}
                        hideCalendar={() => (
                            setCalendarIsShown(false),
                            setPageMonth(format(selectedDate, MONTH_FORMAT_DATE))
                        )}
                        fromMonth={new Date()}
                        selected={selectedDate}
                        month={pageMonth ? newDateFromString(pageMonth) : selectedDate}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        onSelect={(date: Date) => {
                            setSelectedColumn(1);
                            updateDate(date);
                            setCalendarIsShown(false);
                        }}
                        goToPrevWeek={prevWeek}
                        goToNextWeek={nextWeek}
                        prevWeekDisabled={
                            isDateLoading ||
                            !date.from ||
                            startOfDay(date.from).toString() === startOfDay(new Date()).toString()
                        }
                        nextWeekDisabled={isDateLoading}
                        currentMonthDisabled={isDateLoading}
                        onDayMouseEnter={handleDayMouseEnter}
                        onDayMouseLeave={handleDayMouseLeave}
                        disabled={isPastDay}
                        onMonthChange={(newDate) => {
                            setPageMonth(format(newDate, MONTH_FORMAT_DATE));
                        }}
                    />
                </div>
                {isActivitiesLoading ? (
                    <BrandLoader className="h-[60vh]" />
                ) : (
                    <div className="mt-5 md:mt-3">
                        {!listToShow.length ? (
                            <EmptyDataText
                                text="Wait a moment, we are updating data. Refresh the page to see the
                         results."
                            />
                        ) : (
                            <div className="custom-scroll mx-auto mb-8 w-full max-w-[1440px] overflow-hidden md:overflow-x-auto md:overflow-y-hidden md:pb-2 md:pt-8">
                                <div className="mx-auto w-max">
                                    <DaysLine
                                        week={date.week}
                                        activeDay={selectedColumn - 1}
                                        selectDay={(value) =>
                                            mobile && setSelectedColumn(value + 1)
                                        }
                                        showActionButtons={!desktop}
                                        goToPrevDay={() => {
                                            const el =
                                                document.querySelector('#calendar_days_line');

                                            if (el?.scrollLeft) {
                                                el.scrollTo({
                                                    left: 0,
                                                    behavior: 'smooth',
                                                });
                                            } else {
                                                if (
                                                    startOfDay(date.from as Date).toString() !==
                                                    startOfDay(new Date()).toString()
                                                ) {
                                                    prevWeek();
                                                }
                                            }
                                        }}
                                        goToNextDay={() => {
                                            const el =
                                                document.querySelector('#calendar_days_line');

                                            if (!el) {
                                                return;
                                            }

                                            if (
                                                el.scrollLeft + el.clientWidth + 10 <=
                                                el.scrollWidth
                                            ) {
                                                el.scrollTo({
                                                    left: el.scrollWidth,
                                                    behavior: 'smooth',
                                                });
                                            } else {
                                                nextWeek();
                                                el.scrollTo({
                                                    left: 0,
                                                    behavior: 'smooth',
                                                });
                                            }
                                        }}
                                        dayIsDisabled={!mobile}
                                        availList={availList}
                                    />
                                    <div
                                        data-test-id="calendar-columns"
                                        className="flex justify-center md:flex md:[&>*:not(:first-child)]:ml-1 md:[&>*:not(:first-child)]:border-l-[1.2px] md:[&>*:not(:first-child)]:border-gray-3 md:[&>*:not(:first-child)]:dark:border-dark-1"
                                    >
                                        {list.map((sd, idx) => (
                                            <CalendarColumn
                                                key={idx}
                                                list={sd}
                                                selected={
                                                    selectedColumn === idx + 1 ||
                                                    (mobile && !!selectedColumn)
                                                }
                                                selectColumn={() =>
                                                    !mobile && setSelectedColumn(idx + 1)
                                                }
                                                columnDate={date.week[idx || selectedColumn - 1]}
                                                viewBy={viewBy}
                                                selectNextAvailDay={selectNextAvailDay}
                                                setHideButtons={setHideButtons}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};
